/**
 * Validation utilities for the AvoDo Todo App
 */

/**
 * Validates an email address
 * @param {string} email - The email to validate
 * @returns {{valid: boolean, error: string}} Validation result
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string') {
    return { valid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim();

  if (trimmedEmail.length === 0) {
    return { valid: false, error: 'Email is required' };
  }

  if (trimmedEmail.length > 254) {
    return { valid: false, error: 'Email is too long (max 254 characters)' };
  }

  // RFC 5322 simplified regex for email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(trimmedEmail)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }

  return { valid: true, error: '' };
}

/**
 * Validates a password
 * @param {string} password - The password to validate
 * @returns {{valid: boolean, error: string}} Validation result
 */
export function validatePassword(password) {
  if (!password || typeof password !== 'string') {
    return { valid: false, error: 'Password is required' };
  }

  if (password.length < 6) {
    return { valid: false, error: 'Password must be at least 6 characters' };
  }

  if (password.length > 128) {
    return { valid: false, error: 'Password is too long (max 128 characters)' };
  }

  return { valid: true, error: '' };
}

/**
 * Validates todo text
 * @param {string} text - The todo text to validate
 * @returns {{valid: boolean, error: string}} Validation result
 */
export function validateTodoText(text) {
  if (!text || typeof text !== 'string') {
    return { valid: false, error: 'Todo text is required' };
  }

  const trimmedText = text.trim();

  if (trimmedText.length === 0) {
    return { valid: false, error: 'Todo text cannot be empty' };
  }

  if (trimmedText.length > 500) {
    return { valid: false, error: 'Todo text is too long (max 500 characters)' };
  }

  return { valid: true, error: '' };
}

/**
 * Returns user-friendly error messages for Firebase error codes
 * @param {string} errorCode - The Firebase error code
 * @returns {string} User-friendly error message
 */
export function getFirebaseErrorMessage(errorCode) {
  const errorMessages = {
    // Auth errors
    'auth/email-already-in-use': 'This email is already registered. Please login instead.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
    'auth/weak-password': 'Password is too weak. Please use at least 6 characters.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Invalid email or password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed before completion.',
    'auth/cancelled-popup-request': 'Only one popup request is allowed at a time.',

    // Firestore errors
    'permission-denied': 'You do not have permission to perform this action.',
    'not-found': 'The requested document was not found.',
    'already-exists': 'The document already exists.',
    'resource-exhausted': 'Quota exceeded. Please try again later.',
    'failed-precondition': 'Operation failed. Please try again.',
    'aborted': 'Operation was aborted. Please try again.',
    'out-of-range': 'Operation was attempted past the valid range.',
    'unimplemented': 'This operation is not implemented.',
    'internal': 'Internal error. Please try again later.',
    'unavailable': 'Service is currently unavailable. Please try again later.',
    'data-loss': 'Unrecoverable data loss or corruption.',
    'unauthenticated': 'You must be logged in to perform this action.',

    // Generic errors
    'cancelled': 'Operation was cancelled.',
    'unknown': 'An unknown error occurred. Please try again.',
    'invalid-argument': 'Invalid data provided. Please check your input.',
    'deadline-exceeded': 'Operation took too long. Please try again.',
  };

  return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
}
